import {
  WEB_INTERFACE_MESSAGES,
  interfaceMessage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

type InterfaceTranslator = InterfaceLanguageContextValue['t'];

/**
 * FE-80.1 — an OPAQUE IDENTIFIER whose members happen to be spelled in
 * English. Nothing may render one.
 *
 * ⚠ The arms are load-bearing identifiers, not display text: the overlay's
 * fixtures type them, `areaId()` derives the DOM id every heading's
 * `aria-labelledby` points at, and a translated id would break that pairing on
 * every non-English catalogue with nothing red in English. Narrowing the union
 * to lower-case ids was measured and rejected — it turns an unedited sibling
 * suite red at `pnpm typecheck` while Vitest, which strips types, stays green.
 *
 * `KEYBOARD_SHORTCUT_AREA_LABEL_KEYS` maps each arm to the catalogue key that
 * names it, and `ShortcutsOverlay` resolves that key with `t`.
 */
export type KeyboardShortcutArea =
  'Board' | 'Cards' | 'Card detail' | 'Navigation' | 'Activity' | 'Editing';

/**
 * Total over the union — every arm has a key, and the pin asserts the totality
 * rather than trusting the `Record`, because a widened union would be caught
 * by the compiler but a key that is not declared in the catalogue would not.
 */
export const KEYBOARD_SHORTCUT_AREA_LABEL_KEYS: Record<KeyboardShortcutArea, string> = {
  Board: 'shellShortcuts.area.board',
  Cards: 'shellShortcuts.area.cards',
  'Card detail': 'shellShortcuts.area.cardDetail',
  Navigation: 'shellShortcuts.area.navigation',
  Activity: 'shellShortcuts.area.activity',
  Editing: 'shellShortcuts.area.editing',
};

/**
 * FE-80.1 — the nine key caps that are WORDS rather than the name of a key.
 *
 * ⚠ The distinction is the whole slice. `Z`, `1-9`, `/`, `\`, `]` and `?` name
 * the character PRINTED ON THE KEYBOARD and must stay literal: a translated
 * single-letter cap tells a member to press a key their keyboard does not
 * have. These nine are ordinary interface words, rendered raw inside `<kbd>`
 * and folded into the overlay's accessible name, so they are the only caps
 * that a non-English catalogue must be able to reach.
 */
export type KeyboardShortcutKeyCap =
  'Cmd' | 'Ctrl' | 'Down' | 'Enter' | 'Esc' | 'Scroll' | 'Shift' | 'Space' | 'Up';

/**
 * Total over the union, the same twin shape `KEYBOARD_SHORTCUT_AREA_LABEL_KEYS`
 * uses one screen above and for the same reason: the compiler catches a widened
 * union, but only the pin catches a key that no catalogue entry declares.
 */
export const KEYBOARD_SHORTCUT_KEY_CAP_KEYS: Record<KeyboardShortcutKeyCap, string> = {
  Cmd: 'shellShortcuts.keyCap.cmd',
  Ctrl: 'shellShortcuts.keyCap.ctrl',
  Down: 'shellShortcuts.keyCap.down',
  Enter: 'shellShortcuts.keyCap.enter',
  Esc: 'shellShortcuts.keyCap.esc',
  Scroll: 'shellShortcuts.keyCap.scroll',
  Shift: 'shellShortcuts.keyCap.shift',
  Space: 'shellShortcuts.keyCap.space',
  Up: 'shellShortcuts.keyCap.up',
};

/**
 * The modifier joiner, and it stays literal on purpose: it is punctuation, not
 * a word, and it is also the token this resolver SPLITS on — a catalogue that
 * could change it could emit a spec nothing can parse.
 */
const KEY_CAP_SEPARATOR = '+';

function isKeyCap(token: string): token is KeyboardShortcutKeyCap {
  return Object.hasOwn(KEYBOARD_SHORTCUT_KEY_CAP_KEYS, token);
}

/**
 * One `keyCaps` SPEC (`'Shift+Z'`, `'Esc'`, `'1-9'`) to the string the overlay
 * prints. Every token that names a word cap is resolved; every other token —
 * the letters, the digits, the punctuation — survives byte for byte.
 */
export function resolveKeyCap(spec: string, t: InterfaceTranslator): string {
  return spec
    .split(KEY_CAP_SEPARATOR)
    .map((token) => (isKeyCap(token) ? t(KEYBOARD_SHORTCUT_KEY_CAP_KEYS[token]) : token))
    .join(KEY_CAP_SEPARATOR);
}

export type KeyboardShortcutAvailability = 'available' | 'disabled' | 'unavailable';

/**
 * `label` is a RESOLVED sentence and `keys` are RESOLVED caps — never keys and
 * never specs. See `shortcutInventory`.
 */
export interface KeyboardShortcutDefinition {
  id: string;
  area: KeyboardShortcutArea;
  keys: readonly string[];
  label: string;
  singleKey: boolean;
  availability?: KeyboardShortcutAvailability;
}

export interface KeyboardShortcutContext {
  canEdit: boolean;
  canUseCardTarget: boolean;
  canUseWatch: boolean;
  canUseDates: boolean;
  canUseLabels: boolean;
  canUseMembers: boolean;
  singleKeyShortcutsDisabled: boolean;
  platform: 'mac' | 'other';
}

export interface OpenKeyboardShortcutsDetail {
  returnFocus?: HTMLElement | null | undefined;
  context?: KeyboardShortcutContext | undefined;
}

export const OPEN_KEYBOARD_SHORTCUTS_EVENT = 'molt:open-keyboard-shortcuts';

export function openKeyboardShortcuts(detail: OpenKeyboardShortcutsDetail = {}): void {
  if (typeof document === 'undefined') return;
  document.dispatchEvent(
    new CustomEvent<OpenKeyboardShortcutsDetail>(OPEN_KEYBOARD_SHORTCUTS_EVENT, {
      detail,
    }),
  );
}

/**
 * ⚠ A factory yields a catalogue KEY, not a sentence. A module constant cannot
 * call `t`, so the constant holds the key and `shortcutInventory` resolves it —
 * the shape `workspace-switcher` settled, here at 28×.
 *
 * ⚠ AND THE SAME, ONE FIELD OVER, FOR THE CAPS. `keyCaps` holds `+`-joined
 * SPECS; `shortcutInventory` resolves each into the printed `keys`. The RENAME
 * is load-bearing and is not cosmetic: `keyCaps` and `keys` have the identical
 * type, so a factory that still spelled the field `keys` would put an
 * unresolved spec straight into the DOM with `tsc` silent. Renaming makes the
 * omission a type error, exactly as `labelKey` does for `label`.
 */
type ShortcutFactory = (context: KeyboardShortcutContext) => Omit<
  KeyboardShortcutDefinition,
  'availability' | 'label' | 'keys'
> & {
  labelKey: string;
  keyCaps: readonly string[];
  availability?: KeyboardShortcutAvailability | undefined;
};

const shortcutFactories: readonly ShortcutFactory[] = [
  () => ({
    id: 'shortcuts.open',
    area: 'Navigation',
    keyCaps: ['?', 'Shift+?'],
    labelKey: 'shellShortcuts.shortcut.shortcutsOpen',
    singleKey: false,
  }),
  () => ({
    id: 'navigation.board-switcher',
    area: 'Navigation',
    keyCaps: ['B'],
    labelKey: 'shellShortcuts.shortcut.navigationBoardSwitcher',
    singleKey: true,
    // Implemented in `__root.tsx`'s g-prefix handler; opens `BoardSwitcher`.
  }),
  () => ({
    id: 'navigation.search',
    area: 'Navigation',
    keyCaps: ['/'],
    labelKey: 'shellShortcuts.shortcut.navigationSearch',
    singleKey: true,
  }),
  () => ({
    id: 'navigation.planner',
    area: 'Navigation',
    keyCaps: ['G', 'P'],
    labelKey: 'shellShortcuts.shortcut.navigationPlanner',
    singleKey: true,
  }),
  () => ({
    id: 'board.filter.open',
    area: 'Board',
    keyCaps: ['F'],
    labelKey: 'shellShortcuts.shortcut.boardFilterOpen',
    singleKey: true,
  }),
  () => ({
    id: 'board.filter.clear',
    area: 'Board',
    keyCaps: ['X'],
    labelKey: 'shellShortcuts.shortcut.boardFilterClear',
    singleKey: true,
  }),
  () => ({
    id: 'board.filter.my-cards',
    area: 'Board',
    keyCaps: ['Q'],
    labelKey: 'shellShortcuts.shortcut.boardFilterMyCards',
    singleKey: true,
  }),
  () => ({
    id: 'board.list-collapse.toggle',
    area: 'Board',
    keyCaps: ['\\'],
    labelKey: 'shellShortcuts.shortcut.boardListCollapseToggle',
    singleKey: true,
  }),
  () => ({
    id: 'board.horizontal-scroll',
    area: 'Board',
    keyCaps: ['Shift+Scroll'],
    labelKey: 'shellShortcuts.shortcut.boardHorizontalScroll',
    singleKey: false,
  }),
  () => ({
    id: 'activity.board-menu',
    area: 'Activity',
    keyCaps: [']'],
    labelKey: 'shellShortcuts.shortcut.activityBoardMenu',
    singleKey: true,
  }),
  (context) => ({
    id: 'cards.open',
    area: 'Cards',
    keyCaps: ['Enter'],
    labelKey: 'shellShortcuts.shortcut.cardsOpen',
    singleKey: true,
    availability: cardTargetAvailability(context),
  }),
  (context) => ({
    id: 'cards.add-below',
    area: 'Cards',
    keyCaps: ['N'],
    labelKey: 'shellShortcuts.shortcut.cardsAddBelow',
    singleKey: true,
    availability: editCardTargetAvailability(context, 'unavailable'),
  }),
  (context) => ({
    id: 'cards.edit-title',
    area: 'Cards',
    keyCaps: ['T'],
    labelKey: 'shellShortcuts.shortcut.cardsEditTitle',
    singleKey: true,
    availability: editCardTargetAvailability(context, 'unavailable'),
  }),
  (context) => ({
    id: 'cards.quick-edit',
    area: 'Cards',
    keyCaps: ['E'],
    labelKey: 'shellShortcuts.shortcut.cardsQuickEdit',
    singleKey: true,
    availability: editCardTargetAvailability(context),
  }),
  (context) => ({
    id: 'cards.dates',
    area: 'Cards',
    keyCaps: ['D'],
    labelKey: 'shellShortcuts.shortcut.cardsDates',
    singleKey: true,
    availability: editCardTargetAvailability(
      context,
      context.canUseDates ? 'available' : 'unavailable',
    ),
  }),
  (context) => ({
    id: 'cards.labels',
    area: 'Cards',
    keyCaps: ['L'],
    labelKey: 'shellShortcuts.shortcut.cardsLabels',
    singleKey: true,
    availability: editCardTargetAvailability(context, 'unavailable'),
  }),
  (context) => ({
    id: 'cards.label-position',
    area: 'Cards',
    keyCaps: ['1-9', '0'],
    labelKey: 'shellShortcuts.shortcut.cardsLabelPosition',
    singleKey: true,
    availability: editCardTargetAvailability(context, 'unavailable'),
  }),
  (context) => ({
    id: 'cards.members',
    area: 'Cards',
    keyCaps: ['M'],
    labelKey: 'shellShortcuts.shortcut.cardsMembers',
    singleKey: true,
    availability: editCardTargetAvailability(context, 'unavailable'),
  }),
  (context) => ({
    id: 'cards.toggle-self',
    area: 'Cards',
    keyCaps: ['Space'],
    labelKey: 'shellShortcuts.shortcut.cardsToggleSelf',
    singleKey: true,
    availability: editCardTargetAvailability(context, 'unavailable'),
  }),
  (context) => ({
    id: 'cards.watch',
    area: 'Cards',
    keyCaps: ['S'],
    labelKey: 'shellShortcuts.shortcut.cardsWatch',
    singleKey: true,
    availability: editCardTargetAvailability(context, 'unavailable'),
  }),
  (context) => ({
    id: 'cards.archive',
    area: 'Cards',
    keyCaps: ['C'],
    labelKey: 'shellShortcuts.shortcut.cardsArchive',
    singleKey: true,
    availability: editCardTargetAvailability(context),
  }),
  () => ({
    id: 'cards.select-next',
    area: 'Cards',
    keyCaps: ['J', 'Down'],
    labelKey: 'shellShortcuts.shortcut.cardsSelectNext',
    singleKey: true,
    availability: 'unavailable',
  }),
  () => ({
    id: 'cards.select-previous',
    area: 'Cards',
    keyCaps: ['K', 'Up'],
    labelKey: 'shellShortcuts.shortcut.cardsSelectPrevious',
    singleKey: true,
    availability: 'unavailable',
  }),
  () => ({
    id: 'card-detail.close',
    area: 'Card detail',
    keyCaps: ['Esc'],
    labelKey: 'shellShortcuts.shortcut.cardDetailClose',
    singleKey: false,
  }),
  /*
   * ⚠ NO `availability` ON EITHER ARM, and their absence is the fix.
   *
   * Both were declared `'unavailable'` while `BoardFace.tsx`'s `window`
   * `keydown` handler had implemented them for several slices — `z` to
   * `undoRedo.undo()`, `Shift+z` to `undoRedo.redo()` — so the overlay told
   * every member that the working key does not exist. Nothing could see it:
   * the catalogue and the handler are different files, no pin compares them,
   * and the overlay renders whatever the table says.
   *
   * Omitted rather than spelled `'available'`, because `shortcutInventory`
   * defaults `shortcut.availability ?? 'available'` and every other
   * unconditionally-implemented entry in this table is written that way.
   *
   * `singleKey` is deliberately UNCHANGED on both. `editing.undo` keeps
   * `true`, so the overlay correctly reports `'disabled'` when single-key
   * shortcuts are off; `editing.redo` keeps `false`, matching
   * `shortcuts.open`'s `['?', 'Shift+?']` — the house convention that a
   * Shift-modified key is not a single-key shortcut. `BoardFace` currently
   * suppresses `Shift+Z` under `shouldSuppressSingleKeyShortcuts` anyway,
   * which by that convention makes the HANDLER the thing that is off, not
   * this row. Recorded as a residual rather than repaired here: splitting the
   * suppression check across the two arms changes an accessibility preference
   * and wants its own argument.
   */
  () => ({
    id: 'editing.undo',
    area: 'Editing',
    keyCaps: ['Z'],
    labelKey: 'shellShortcuts.shortcut.editingUndo',
    singleKey: true,
  }),
  () => ({
    id: 'editing.redo',
    area: 'Editing',
    keyCaps: ['Shift+Z'],
    labelKey: 'shellShortcuts.shortcut.editingRedo',
    singleKey: false,
  }),
  () => ({
    id: 'editing.repeat',
    area: 'Editing',
    keyCaps: ['R'],
    labelKey: 'shellShortcuts.shortcut.editingRepeat',
    singleKey: true,
    availability: 'unavailable',
  }),
  (context) => ({
    id: 'editing.submit',
    area: 'Editing',
    keyCaps: [`${context.platform === 'mac' ? 'Cmd' : 'Ctrl'}+Enter`],
    labelKey: 'shellShortcuts.shortcut.editingSubmit',
    singleKey: false,
  }),
];

export function platformShortcutContext(userAgent?: string): 'mac' | 'other' {
  const value =
    userAgent ??
    (typeof navigator === 'undefined'
      ? ''
      : `${navigator.userAgent} ${navigator.platform}`);
  return /\b(Mac|iPhone|iPad|iPod)\b/i.test(value) ? 'mac' : 'other';
}

/**
 * The source-English resolver used when no translator is supplied.
 *
 * ⚠ Exported, and the export is the point: `shortcutInventory` takes an
 * OPTIONAL translator because its oracle calls it with one argument, and the
 * two shell menu-section builders resolve their own optional `t` the same way.
 * One spelling of "resolve a key with no React context in reach" is what keeps
 * a second hand-copied English table from growing back.
 */
export function sourceInterfaceMessage(
  key: string,
  values?: Record<string, string | number>,
  count?: number,
): string {
  const message = WEB_INTERFACE_MESSAGES[key];
  if (message === undefined) return key;
  return interfaceMessage(message, values, count);
}

export function shortcutInventory(
  context: KeyboardShortcutContext,
  t: InterfaceTranslator = sourceInterfaceMessage,
): readonly KeyboardShortcutDefinition[] {
  return shortcutFactories.map((build) => {
    const { labelKey, keyCaps, ...shortcut } = build(context);
    const baseAvailability = shortcut.availability ?? 'available';
    const availability =
      baseAvailability === 'available' &&
      shortcut.singleKey &&
      context.singleKeyShortcutsDisabled
        ? 'disabled'
        : baseAvailability;

    return {
      ...shortcut,
      availability,
      keys: keyCaps.map((spec) => resolveKeyCap(spec, t)),
      label: t(labelKey),
    };
  });
}

export function isEditableShortcutTarget(target: EventTarget | null): boolean {
  if (typeof HTMLElement === 'undefined' || !(target instanceof HTMLElement)) {
    return false;
  }
  return (
    target.closest(
      'input, textarea, select, [contenteditable="true"], [role="textbox"], [role="searchbox"], [role="combobox"]',
    ) !== null || target.isContentEditable === true
  );
}

export function shouldSuppressSingleKeyShortcuts(context: {
  singleKeyShortcutsDisabled: boolean;
  target: EventTarget | null;
}): boolean {
  return context.singleKeyShortcutsDisabled || isEditableShortcutTarget(context.target);
}

function cardTargetAvailability({
  canUseCardTarget,
}: KeyboardShortcutContext): KeyboardShortcutAvailability {
  return canUseCardTarget ? 'available' : 'unavailable';
}

function editCardTargetAvailability(
  { canEdit, canUseCardTarget }: KeyboardShortcutContext,
  implemented: KeyboardShortcutAvailability = 'available',
): KeyboardShortcutAvailability {
  if (!canUseCardTarget || !canEdit) return 'unavailable';
  return implemented;
}
